// Single place for site tracking: GTM (+ Consent Mode v2), page views, CTA clicks, conversions and UTM/click-ID attribution.
// GA4, Google Ads, Meta, TikTok, Hotjar and Reddit all run inside GTM, never add their tags directly here (double counting).
export const GTM_ID = process.env.REACT_APP_GTM_ID || "GTM-5NQ295T4";
export const CONSENT_COOKIE = "stl_consent";
const PROD_HOSTS = ["skysthelimit.org", "www.skysthelimit.org", "blog.skysthelimit.org"];
const ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "ttclid", "rdt_cid", "msclkid"];
const UTM_KEYS = ATTR_KEYS.slice(0, 5);
const FIRST_KEYS = ["first_landing_page", "first_referrer"];
const CTA_RE = /app\.skysthelimit\.org|\/donate|\/daf|\/partnerships|\/powered-by/;
const HS_FORMS = { "1d5627b3-f3ca-4374-a538-c5434c5f292e": "partnership_inquiry", "ef699d84-2dd7-4aca-969c-3ed5413baba4": "powered_by_inquiry" };
const DEBUG_KEY = "stl_analytics_debug";
// EEA + UK + CH: consent denied until the visitor chooses; everywhere else defaults to granted.
const CONSENT_REGIONS = ["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IS", "IE", "IT", "LV", "LI", "LT", "LU", "MT", "NL", "NO", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "GB", "CH"];

export const isProd = () => typeof window !== "undefined" && PROD_HOSTS.includes(window.location.hostname);

/* Preview-only opt-in (?analytics_debug=1, off with =0): mirrors events into window.dataLayer and shows the debug panel + banner. GTM never loads outside production. */
export function isDebug() {
  if (isProd() || typeof window === "undefined") return false;
  const q = new URLSearchParams(window.location.search).get("analytics_debug");
  try {
    if (q === "1") localStorage.setItem(DEBUG_KEY, "1");
    if (q === "0") localStorage.removeItem(DEBUG_KEY);
    return localStorage.getItem(DEBUG_KEY) === "1";
  } catch { return q === "1"; }
}

const usesDataLayer = () => isProd() || isDebug();

/* ---------- cookies ---------- */
export function readCookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}
export function writeCookie(name, value, maxAge) {
  const domain = isProd() ? "; domain=.skysthelimit.org" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}${domain}; path=/; max-age=${maxAge}; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
}

/* ---------- consent ---------- */
// analytics left undecided (GPC) keeps the region default for analytics_storage.
export const consentState = ({ analytics, marketing }) => ({
  ...(typeof analytics === "boolean" && { analytics_storage: analytics ? "granted" : "denied" }),
  ad_storage: marketing ? "granted" : "denied",
  ad_user_data: marketing ? "granted" : "denied",
  ad_personalization: marketing ? "granted" : "denied",
});

export function readConsent() {
  try { const c = JSON.parse(readCookie(CONSENT_COOKIE) || "null"); return c && typeof c === "object" ? c : null; } catch { return null; }
}

export function saveConsent(choice) {
  const value = choice.gpc
    ? { marketing: false, gpc: true, ts: Date.now() }
    : { analytics: !!choice.analytics, marketing: !!choice.marketing, ts: Date.now() };
  writeCookie(CONSENT_COOKIE, JSON.stringify(value), 31536000);
  if (window.gtag) window.gtag("consent", "update", consentState(value));
  track("consent_update", { analytics: value.analytics ?? "default", marketing: value.marketing, consent_source: value.gpc ? "gpc" : "banner" });
  window.dispatchEvent(new Event("stl:consent"));
  return value;
}

export const openCookieSettings = () => window.dispatchEvent(new Event("stl:open-cookie-settings"));

/* ---------- events ---------- */
export function track(event, params = {}) {
  if (typeof window === "undefined") return;
  if (!isProd()) console.debug("[analytics]", event, params);
  if (usesDataLayer()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
    window.dispatchEvent(new Event("stl:datalayer"));
  }
}

/* ---------- attribution (replaces HubSpot's header script) ---------- */
export function captureAttribution(search = window.location.search) {
  const q = new URLSearchParams(search);
  ATTR_KEYS.forEach((k) => { const v = q.get(k); if (v) writeCookie(k, v, 2592000); });
  if (!readCookie("first_landing_page")) writeCookie("first_landing_page", window.location.pathname + window.location.search, 2592000);
  if (!readCookie("first_referrer")) writeCookie("first_referrer", document.referrer || "(direct)", 2592000);
}

export function getStoredAttribution() {
  const out = {};
  [...ATTR_KEYS, ...FIRST_KEYS].forEach((k) => { const v = readCookie(k); if (v) out[k] = v; });
  return out;
}

/* For future admin-portal forms. Pass only non-personal fields in `extra`. */
export const trackFormSubmit = (formName, extra = {}) => track("generate_lead", { form_name: formName, ...getStoredAttribution(), ...extra });

function appendUtms(a) {
  try {
    const url = new URL(a.href);
    let changed = false;
    UTM_KEYS.forEach((k) => {
      const v = readCookie(k);
      if (v && !url.searchParams.has(k)) { url.searchParams.set(k, v); changed = true; }
    });
    if (changed) a.href = url.toString();
  } catch { /* ignore malformed hrefs */ }
}

function onDocumentClick(e) {
  const a = e.target instanceof Element ? e.target.closest("a[href]") : null;
  if (!a) return;
  const href = a.getAttribute("href") || "";
  if (/app\.skysthelimit\.org/.test(a.href)) appendUtms(a);
  if (CTA_RE.test(href) || CTA_RE.test(a.href) || a.hasAttribute("data-cta")) {
    track("cta_click", { cta_text: (a.innerText || a.getAttribute("aria-label") || "").trim().slice(0, 100), destination_url: a.href, page_path: window.location.pathname });
  }
}

// TEMP: remove when admin-portal forms replace HubSpot
let lastHsLead = { id: null, at: 0 };
function trackHubspotLead(formId) {
  if (formId === lastHsLead.id && Date.now() - lastHsLead.at < 3000) return;
  lastHsLead = { id: formId, at: Date.now() };
  track("generate_lead", { form_name: HS_FORMS[formId] || "hubspot_form", form_source: "hubspot" });
}
// TEMP: legacy embeds (postMessage)
function onHubspotMessage(event) {
  if (event.data?.type === "hsFormCallback" && event.data?.eventName === "onFormSubmitted") trackHubspotLead(event.data.id);
}
// TEMP: new-editor embeds (Global Form Events; our /partnerships and /powered-by forms)
const onHubspotSuccess = (event) => trackHubspotLead(event.detail?.formId);

/* ---------- boot ---------- */
let started = false;
export function initAnalytics() {
  if (started || typeof window === "undefined") return;
  started = true;
  captureAttribution();
  document.addEventListener("click", onDocumentClick, true);
  window.addEventListener("message", onHubspotMessage);
  window.addEventListener("hs-form-event:on-submission:success", onHubspotSuccess);
  if (!usesDataLayer()) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
  window.gtag("consent", "default", { ad_storage: "granted", analytics_storage: "granted", ad_user_data: "granted", ad_personalization: "granted" });
  window.gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", wait_for_update: 500, region: CONSENT_REGIONS });
  const saved = readConsent();
  if (saved) window.gtag("consent", "update", consentState(saved));
  else if (navigator.globalPrivacyControl === true) saveConsent({ gpc: true });
  if (!isProd()) return;

  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}
